import React from 'react';
import { Alert, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import { FDataGrid } from '../grid';
import ForecastMethodGuide from './ForecastMethodGuide';
import {
  IForecastVariableRow,
} from './bieforecasting.models';

type Props = {
  rows: IForecastVariableRow[];
  loading?: boolean;
  editable?: boolean;
  onRowsChange: (rows: IForecastVariableRow[]) => void;
  onSave: (rows: IForecastVariableRow[]) => Promise<void>;
};

type State = { error: string };

const METHOD_LABELS: Record<string, string> = {
  HISTORICAL_AVERAGE: 'Historical Average',
  MOVING_AVERAGE: 'Moving Average',
  LINEAR_TREND: 'Trend',
  SEASONAL_TREND: 'Seasonal Trend',
  SEASONAL_INDEX: 'Seasonal Trend',
  LAST_VALUE: 'Last Value',
  GROWTH_RATE: 'Growth Rate',
  MANAGEMENT_ESTIMATE: 'Management Estimate',
  FIXED_ASSUMPTION: 'Fixed Assumption',
};

const METHOD_OPTIONS = [
  'HISTORICAL_AVERAGE',
  'MOVING_AVERAGE',
  'LINEAR_TREND',
  'SEASONAL_TREND',
  'LAST_VALUE',
  'GROWTH_RATE',
  'MANAGEMENT_ESTIMATE',
  'FIXED_ASSUMPTION',
].map((value) => ({ value, label: METHOD_LABELS[value] || value }));

class ForecastVariableGrid extends React.PureComponent<Props, State> {
  state: State = { error: '' };

  private validate = (row: IForecastVariableRow): string => {
    if (!row.forecastmethod) return 'Forecast method is required.';
    if (row.forecastmethod === 'GROWTH_RATE' && row.growthrate == null) {
      return 'Growth % is required for Growth Rate.';
    }
    if ((row.forecastmethod === 'FIXED_ASSUMPTION' || row.forecastmethod === 'MANAGEMENT_ESTIMATE') && row.fixedvalue == null) {
      return 'Forecast Value is required for this method.';
    }
    if (row.recommendedmethod && row.forecastmethod !== row.recommendedmethod && !String(row.overrideReason || '').trim()) {
      return 'Explain why the recommendation is being overridden.';
    }
    if (row.effectivefrom && row.effectiveto && row.effectivefrom > row.effectiveto) {
      return 'Effective From cannot be after Effective To.';
    }
    return '';
  };

  private normalizeParameters = (row: IForecastVariableRow): IForecastVariableRow => {
    const method = row.forecastmethod;
    return {
      ...row,
      growthrate: method === 'GROWTH_RATE' ? row.growthrate : null,
      fixedvalue: (method === 'FIXED_ASSUMPTION' || method === 'MANAGEMENT_ESTIMATE') ? row.fixedvalue : null,
      seasonality: method === 'SEASONAL_TREND',
      overrideReason: row.recommendedmethod && method === row.recommendedmethod ? '' : row.overrideReason,
    };
  };

  private processRowUpdate = (newRow: GridValidRowModel): GridValidRowModel => {
    const row = this.normalizeParameters({ ...newRow } as IForecastVariableRow);
    const error = this.validate(row);
    const updated = { ...row, __dirty: true, __error: error };
    this.props.onRowsChange(this.props.rows.map((x) => x.id === updated.id ? updated : x));
    return updated;
  };

  private save = async () => {
    const rows = this.props.rows.map((row) => {
      const normalized = this.normalizeParameters(row);
      return { ...normalized, __error: this.validate(normalized) };
    });
    this.props.onRowsChange(rows);

    const invalid = rows.find((row) => row.__error);
    if (invalid) {
      this.setState({ error: `${invalid.variablecode}: ${invalid.__error}` });
      return;
    }

    this.setState({ error: '' });
    await this.props.onSave(rows);
  };

  private confidenceColor = (confidence?: string): 'success' | 'warning' | 'default' => {
    if (confidence === 'HIGH') return 'success';
    if (confidence === 'MEDIUM') return 'warning';
    return 'default';
  };

  private columns = (): GridColDef[] => [
    { field: 'variablecode', headerName: 'Code', width: 135 },
    { field: 'variablename', headerName: 'Variable', flex: 1, minWidth: 220 },
    { field: 'source', headerName: 'Source', width: 105 },
    {
      field: 'recommendedmethod',
      headerName: 'Recommended Method',
      width: 205,
      renderCell: (p) => (
        <Chip
          size="small"
          variant="outlined"
          label={METHOD_LABELS[String(p.value || '')] || String(p.value || 'Review')}
        />
      ),
    },
    {
      field: 'recommendationconfidence',
      headerName: 'Confidence',
      width: 115,
      renderCell: (p) => (
        <Chip
          size="small"
          color={this.confidenceColor(String(p.value || 'LOW'))}
          label={String(p.value || 'LOW')}
        />
      ),
    },
    {
      field: 'forecastmethod',
      headerName: 'Selected Method',
      width: 190,
      editable: Boolean(this.props.editable),
      type: 'singleSelect',
      valueOptions: METHOD_OPTIONS,
    },
    {
      field: 'growthrate',
      headerName: 'Growth %',
      width: 110,
      type: 'number',
      editable: Boolean(this.props.editable),
      renderCell: (p) => p.row.forecastmethod === 'GROWTH_RATE' ? (p.value ?? '') : <Typography color="text.disabled">—</Typography>,
    },
    {
      field: 'fixedvalue',
      headerName: 'Forecast Value',
      width: 140,
      type: 'number',
      editable: Boolean(this.props.editable),
      renderCell: (p) => ['FIXED_ASSUMPTION', 'MANAGEMENT_ESTIMATE'].includes(p.row.forecastmethod)
        ? (p.value ?? '')
        : <Typography color="text.disabled">—</Typography>,
    },
    {
      field: 'seasonality',
      headerName: 'Seasonal',
      width: 100,
      renderCell: (p) => p.row.forecastmethod === 'SEASONAL_TREND'
        ? <Chip size="small" color="info" label="Yes" />
        : <Typography color="text.disabled">—</Typography>,
    },
    {
      field: 'recommendationreason',
      headerName: 'Recommendation Basis',
      minWidth: 330,
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={String(p.value || '')} placement="top-start">
          <Typography variant="body2" noWrap>{String(p.value || '')}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'overrideReason',
      headerName: 'Override Reason',
      minWidth: 240,
      flex: 1,
      editable: Boolean(this.props.editable),
      renderCell: (p) => p.row.recommendedmethod && p.row.forecastmethod !== p.row.recommendedmethod
        ? String(p.value || '')
        : <Typography color="text.disabled">Not required</Typography>,
    },
    {
      field: '__error',
      headerName: 'Validation',
      minWidth: 220,
      flex: 1,
      renderCell: (p) => p.value
        ? <Chip size="small" color="error" label={String(p.value)} />
        : <Chip size="small" color="success" variant="outlined" label="Ready" />,
    },
  ];

  render() {
    const dirtyCount = this.props.rows.filter((row) => row.__dirty).length;
    return (
      <Stack spacing={1.5}>
        <ForecastMethodGuide />
        {this.state.error && <Alert severity="error">{this.state.error}</Alert>}
        <FDataGrid
          title="Forecast Variable Configuration"
          subtitle="An initial method is recommended from the certified historical pattern. Review, override where necessary, then confirm the governed batch configuration."
          rows={this.props.rows || []}
          columns={this.columns()}
          loading={this.props.loading}
          height={560}
          showQuickFilter
          getRowId={(row) => row.id || row.rid || row.variablecode}
          processRowUpdate={this.processRowUpdate}
          onProcessRowUpdateError={(error) => this.setState({ error: error?.message || String(error) })}
          saveAll={this.props.editable ? this.save : undefined}
          toolbarActions={this.props.editable ? [{
            key: 'save-variables',
            label: dirtyCount > 0
              ? `Save & Confirm (${dirtyCount} Change${dirtyCount === 1 ? '' : 's'})`
              : 'Confirm Forecast Variables',
            icon: <SaveIcon />,
            disabled: Boolean(this.props.loading) || !this.props.rows.length,
            onClick: this.save,
          }] : []}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          getRowClassName={(p) => p.row.__error ? 'error-row' : p.row.__dirty ? 'dirty-row' : ''}
          sx={{
            '& .error-row': { bgcolor: 'error.lighter' },
            '& .dirty-row': { bgcolor: 'warning.lighter' },
          }}
        />
      </Stack>
    );
  }
}

export default ForecastVariableGrid;
